import React, { useState } from 'react';
import { 
  X, 
  Save, 
  Briefcase, 
  Zap, 
  Target, 
  User, 
  Award, 
  Send, 
  Star, 
  Info, 
  AlertCircle 
} from 'lucide-react';

const ProjectDetailsModal = ({ 
  candidate, 
  onSave, 
  onClose, 
  useToast, 
  API_BASE_URL, 
  fetchCandidates 
}) => {
  // Get toast functions from the useToast hook
  const { success, error } = useToast();
  
  // ✅ FIXED: Use correct field names that match your database columns
  const [projectTechStack, setProjectTechStack] = useState(candidate?.project_tech_stack || '');
  const [projectOverview, setProjectOverview] = useState(candidate?.project_overview || '');
  const [projectGoals, setProjectGoals] = useState(candidate?.project_goals || '');
  const [projectRoleResponsibility, setProjectRoleResponsibility] = useState(candidate?.project_role_responsibility || '');
  const [keyAchievement, setKeyAchievement] = useState(candidate?.key_achievement || '');
  const [projectExperienceRating, setProjectExperienceRating] = useState(candidate?.project_experience_rating || 0);

  const [errors, setErrors] = useState({});
  const [sectionLoading, setSectionLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  const validateSection2 = () => {
    const newErrors = {};
    
    if (!projectTechStack.trim()) {
      newErrors.projectTechStack = 'Technology stack is required';
    }
    
    if (!projectOverview.trim()) {
      newErrors.projectOverview = 'Project overview is required';
    }
    
    if (!projectGoals.trim()) {
      newErrors.projectGoals = 'Project goals are required';
    }
    
    if (projectExperienceRating < 1 || projectExperienceRating > 5) {
      newErrors.projectExperienceRating = 'Rating must be between 1 and 5';
    }

    if (!projectRoleResponsibility.trim()) {
      newErrors.projectRoleResponsibility = 'Role and responsibility description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const addToHistory = async (candidateId, actionType, fieldName, oldValue, newValue, reason = '', comments = '') => {
  try {
    const response = await fetch(`${API_BASE_URL}/add_candidate_history.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        candidate_id: candidateId,
        candidate_name: candidate?.candidateName || 'Unknown',
        candidate_email: candidate?.candidateEmail || 'unknown@email.com',
        action_type: actionType,
        field_name: fieldName,
        old_value: oldValue || 'Not set',
        new_value: newValue,
        change_reason: reason,
        change_comments: comments || `${fieldName} updated from "${oldValue || 'Not set'}" to "${newValue}"`,
        created_by: 'Current User' // Replace with actual user
      })
    });

    const result = await response.json();
    if (!result.success) {
      console.error('Failed to add history:', result.message);
    } else {
      console.log('✅ History record added:', result.data);
    }
  } catch (error) {
    console.error('Error adding history:', error);
  }
};

  const handleSectionSave = async (section) => {
  console.log('🔄 Starting save process for section:', section);
  console.log('📊 Current form data:', {
    projectTechStack,
    projectOverview,
    projectGoals,
    projectRoleResponsibility,
    keyAchievement,
    projectExperienceRating
  });

  setSectionLoading(true);
  let isValid = false;
  let updateData = {};

  switch (section) {
    case 2:
      isValid = validateSection2();
      console.log('✅ Validation result:', isValid);
      console.log('❌ Validation errors:', errors);
      
      if (isValid) {
  updateData = {
    id: candidate.id,
    projectTechStack: projectTechStack,
    projectOverview: projectOverview,
    projectGoals: projectGoals,
    projectRoleResponsibility: projectRoleResponsibility,
    keyAchievement: keyAchievement,
    projectExperienceRating: projectExperienceRating,
    
    // ✅ CRITICAL FIX: Add candidate identification for proper activity logging
    candidateName: candidate.candidateName || candidate.candidate_name,
    candidateEmail: candidate.candidateEmail || candidate.candidate_email,
    
    // ✅ CRITICAL FIX: Enable project tracking
    enableProjectTracking: true,
    enableProjectCompletion: true,
    enableFeedbackTracking: false,
    enableExtensionStatusCalc: false,
    enableRDForwarding: false,
    enableReportingTracking: false,
    
    // ✅ Prevent unwanted side effects
    preventExtensionStatusUpdate: true
  };
  console.log('📦 Update data prepared for direct API call:', updateData);
}
      break;
    default:
      console.log('❌ Invalid section:', section);
      setSectionLoading(false);
      return;
  }

  if (isValid) {
    try {
      console.log('🚀 Making DIRECT API call to update_candidate.php');
      
      // 🔥 STEP 1: Save the data first
      const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Direct API call successful:', result);
        
        // 🔥 STEP 2: Add history records for each changed field
        const fieldsToTrack = [
          { 
            jsField: 'projectTechStack', 
            oldValue: candidate?.project_tech_stack || '', 
            newValue: projectTechStack,
            displayName: 'Project Tech Stack'
          },
          { 
            jsField: 'projectOverview', 
            oldValue: candidate?.project_overview || '', 
            newValue: projectOverview,
            displayName: 'Project Overview'
          },
          { 
            jsField: 'projectGoals', 
            oldValue: candidate?.project_goals || '', 
            newValue: projectGoals,
            displayName: 'Project Goals'
          },
          { 
            jsField: 'projectRoleResponsibility', 
            oldValue: candidate?.project_role_responsibility || '', 
            newValue: projectRoleResponsibility,
            displayName: 'Project Role & Responsibility'
          },
          { 
            jsField: 'keyAchievement', 
            oldValue: candidate?.key_achievement || '', 
            newValue: keyAchievement,
            displayName: 'Key Achievement'
          },
          { 
            jsField: 'projectExperienceRating', 
            oldValue: candidate?.project_experience_rating || 0, 
            newValue: projectExperienceRating,
            displayName: 'Project Experience Rating'
          }
        ];

        // Add history for each changed field
        for (const field of fieldsToTrack) {
          if (field.oldValue !== field.newValue && field.newValue !== '' && field.newValue !== 0) {
            console.log(`📝 Adding history for ${field.displayName}: "${field.oldValue}" → "${field.newValue}"`);
            
            await addToHistory(
              candidate.id,
              'project_update',
              field.jsField,
              field.oldValue,
              field.newValue,
              'Project details updated via Project Details modal',
              `${field.displayName} updated from "${field.oldValue || 'Not set'}" to "${field.newValue}"`
            );
          }
        }
        
        success(`Project information saved successfully with history tracking! ${fieldsToTrack.filter(f => f.oldValue !== f.newValue && f.newValue !== '' && f.newValue !== 0).length} fields updated.`);
        
        // Refresh candidates list if function exists
        if (typeof fetchCandidates === 'function') {
          console.log('🔄 Refreshing candidates list...');
          await fetchCandidates();
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(result.message || 'Unknown API error');
      }
      
    } catch (err) {
      console.error('❌ Save error details:', {
        message: err.message,
        stack: err.stack,
        candidateId: candidate.id,
        updateData: updateData
      });
      error(`Failed to save: ${err.message}`);
    }
  } else {
    console.log('❌ Validation failed, not saving');
    error('Please fill in all required fields correctly');
  }
  setSectionLoading(false);
};

  const sendEmail = async (emailType, candidateId, additionalData = {}) => {
    try {
      console.log('🚀 Starting sendEmail with:', { emailType, candidateId, additionalData });
      
      if (!emailType) {
        throw new Error('Email type is required');
      }
      
      if (!candidateId) {
        throw new Error('Candidate ID is required');
      }
      
      const payload = {
        emailType,
        candidateId,
        ...additionalData
      };
      
      console.log('📤 Sending payload:', payload);
      
      const response = await fetch(`${API_BASE_URL}/send_email.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Email API HTTP error:', response.status, errorText);
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('📄 Raw response:', responseText);
      
      let result;
      try {
        result = JSON.parse(responseText);
        console.log('✅ Parsed result:', result);
      } catch (parseError) {
        console.error('❌ JSON parse error:', parseError);
        throw new Error(`Invalid response from server: ${responseText.substring(0, 100)}...`);
      }

      if (result.success) {
        console.log('✅ Email sent successfully:', result);
        return {
          success: true,
          recipient: result.details?.recipient || additionalData.candidateEmail || 'Unknown',
          subject: result.details?.subject || 'Email Sent',
          type: emailType,
          message: result.message
        };
      } else {
        console.error('❌ Email send failed:', result.message);
        throw new Error(result.message || 'Unknown email error');
      }

    } catch (error) {
      console.error('❌ Email send error:', error);
      
      let userMessage = error.message;
      
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        userMessage = 'Network error: Unable to connect to email server. Please check if the backend is running.';
      } else if (error.message.includes('CORS')) {
        userMessage = 'CORS error: Please check CORS headers in your PHP configuration.';
      } else if (error.message.includes('Email endpoint is working')) {
        userMessage = 'Email system is online but request failed. Check console for details.';
      }
      
      throw new Error(userMessage);
    }
  };

  const renderSection2 = () => (
    <div className="space-y-8">
      {/* Project Information Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Project Information & Experience</h3>
        </div>
        <p className="text-slate-600 text-sm">Update current project details and technical experience</p>
        
        {/* Instructions */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <Info className="w-3 h-3 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">Instructions</p>
              <p className="text-sm text-blue-800 mb-2">
                Fill out this section with the candidate's technology stack and the contributions they make to the client.
              </p>
              <p className="text-sm text-blue-800">
                If the candidate is unavailable, select <strong>'Forward Form to Candidate'</strong> to send it directly to them for completion.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Zap className="w-4 h-4 mr-2 text-blue-600" />
            Technology Stack *
          </label>
          <p className="text-xs text-slate-600 mb-2">What are the key technologies, frameworks, or tools you're working with on this project?</p>
          <textarea
            value={projectTechStack}
            onChange={(e) => setProjectTechStack(e.target.value)}
            rows={3}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.projectTechStack ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="e.g., React.js, Node.js, AWS, MongoDB, Docker..."
          />
          {errors.projectTechStack && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectTechStack}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
            Project Overview *
          </label>
          <p className="text-xs text-slate-600 mb-2">Can you briefly describe the project and what the client is aiming to achieve?</p>
          <textarea
            value={projectOverview}
            onChange={(e) => setProjectOverview(e.target.value)}
            rows={4}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.projectOverview ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Describe the project's purpose, what the client wants to accomplish, and the overall vision..."
          />
          {errors.projectOverview && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectOverview}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Target className="w-4 h-4 mr-2 text-blue-600" />
            Project Goals *
          </label>
          <p className="text-xs text-slate-600 mb-2">What are the specific outcomes or success metrics for this project?</p>
          <textarea
            value={projectGoals}
            onChange={(e) => setProjectGoals(e.target.value)}
            rows={4}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.projectGoals ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Describe the measurable goals, deliverables, KPIs, or success criteria..."
          />
          {errors.projectGoals && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectGoals}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <User className="w-4 h-4 mr-2 text-blue-600" />
            Your Role & Responsibilities *
          </label>
          <p className="text-xs text-slate-600 mb-2">What is your position in the project, and what are your main responsibilities?</p>
          <textarea
            value={projectRoleResponsibility}
            onChange={(e) => setProjectRoleResponsibility(e.target.value)}
            rows={4}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 ${
              errors.projectRoleResponsibility ? 'border-red-500 bg-red-50' : 'border-slate-300 hover:border-slate-400'
            }`}
            placeholder="Describe your job title, daily tasks, key responsibilities, and how you contribute to the project..."
          />
          {errors.projectRoleResponsibility && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectRoleResponsibility}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Award className="w-4 h-4 mr-2 text-blue-600" />
            Key Achievement or Success Story (Optional)
          </label>
          <p className="text-xs text-slate-600 mb-2">Have you made any notable contributions or reached any significant milestones you'd like to share?</p>
          <textarea
            value={keyAchievement}
            onChange={(e) => setKeyAchievement(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 border-slate-300 hover:border-slate-400"
            placeholder="Share any notable accomplishments, problem-solving successes, innovations, or positive impact you've made on the project..."
          />
        </div>

        <div className="space-y-3">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Star className="w-4 h-4 mr-2 text-blue-600" />
            Project Experience Rating *
          </label>
          <p className="text-xs text-slate-600 mb-2">How would you rate your overall experience working on this project?</p>
          <div className="flex items-center space-x-2 mb-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setProjectExperienceRating(star)}
                className={`p-1 ${star <= projectExperienceRating ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-400 transition-colors`}
              >
                <Star className="w-6 h-6 fill-current" />
              </button>
            ))}
            <span className="ml-3 text-sm text-slate-600">
              {projectExperienceRating > 0 ? `${projectExperienceRating}/5` : 'No rating'}
            </span>
          </div>
          {errors.projectExperienceRating && (
            <div className="flex items-center space-x-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.projectExperienceRating}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-slate-200">
        <button
          onClick={async () => {
            try {
              setEmailSending(true);
              
              // Send email
              await sendEmail('project_info_form', candidate.id, {
                candidateName: candidate.candidateName,
                candidateEmail: candidate.candidateEmail,
                client: candidate.client,
                jobTitle: candidate.jobTitle,
                formSentAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
              });
              
              // Update candidate record with form sent timestamp
              const currentDateTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
              
              await fetch(`${API_BASE_URL}/update_candidate.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: candidate.id,
                  project_form_sent_at: currentDateTime,
                  project_form_reminder_count: 0
                })
              });
              
              success(`Project information form sent successfully to ${candidate.candidateName}! 24-hour reminder system activated.`);
              await fetchCandidates(); // Refresh data
              
            } catch (err) {
              console.error('❌ Project info form send error:', err);
              error(`Failed to send project information form: ${err.message}`);
            } finally {
              setEmailSending(false);
            }
          }}
          disabled={sectionLoading || emailSending}
          className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {emailSending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Sending Form...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Forward Form to Candidate</span>
            </>
          )}
        </button>
        
        <button
          onClick={() => handleSectionSave(2)}
          disabled={sectionLoading || emailSending}
          className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {sectionLoading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              <span>Save Project Information</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  if (!candidate) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden border border-slate-200">
        {/* Enhanced Header */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <Briefcase className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  Project Details
                </h2>
                <p className="text-blue-100 text-sm">{candidate.candidateName} • {candidate.client}</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-3 hover:bg-white/20 rounded-xl transition-all duration-200 group"
            >
              <X className="w-6 h-6 group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
        </div>

        <div className="p-8 max-h-[calc(95vh-200px)] overflow-y-auto">
          {renderSection2()}
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailsModal;